// Home dashboard (Fig29) — a KPI + attention-triage screen, distinct from the
// Projects table. Everything here reads from real query data: KPI tiles,
// the recent-projects list, and the disclosure panels all derive from
// getProjects()/getRecommendations()/getReviews()/getDisclosureAgreements().
// Where a mockup value has no honest backing (e.g. a specific "review N
// extracted claims" count), it is left out rather than fabricated.
import { useState, useEffect, useRef, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus,
  FolderOpen,
  FileText,
  GitBranch,
  ShieldCheck,
  Hourglass,
  ClipboardCheck,
  Lightbulb,
} from 'lucide-react'
import { getRecommendations, refreshRecommendations, getDisclosureAgreements, consentDisclosureAdvance, declineDisclosureAdvance, getReviews, getCachedPortfolioOverlap, listPendingSeats, listMyIpArrangements } from '../../services/api'
import type { Project, DisclosureAgreement } from '../../types'
import { useAuth } from '../../contexts/AuthContext'
import DisclosureLevel from '../graph/DisclosureLevel'
import PageHeader from '../ui/PageHeader'
import EmptyState from '../ui/EmptyState'
import StatusChip from '../ui/StatusChip'
import { useSetSpine, useDefaultLevels, NextStepBanner } from '../spine'
import { organisationName } from '../sharing/sharingModel'
import { acknowledgeHeadline, waitingOnMe } from '../sharing/ipModel'
import GuidedTour, { hasSeenTour, type TourStep } from '../help/GuidedTour'
import { STAGE_INFO } from '../graph/DisclosureRequestModal'
import AtlasSlot from '../ownscope/AtlasSlot'
import StatTile from './StatTile'
import { getProjectStagePill, deriveProjectWorkflowStages, isProjectDisclosed } from '../../utils/projectStage'
import { projectInitials } from '../../utils/projectDisplay'

const HOME_TOUR: TourStep[] = [
  { title: 'Welcome to InnovateFlow', body: 'This is your Home — it surfaces what needs your attention and lists your inventions. Let’s take a quick look at how to get started.' },
  { target: 'new-invention', title: 'Start with an invention', body: 'Select New invention to submit one. A short guided wizard collects your files and a one-line description, then the system runs intake and AI analysis for you.' },
  { title: 'Follow the pipeline', body: 'Each invention moves through a visible pipeline — Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure. Open an invention’s Overview tab to see where it is and what comes next.' },
]

/**
 * "Level 3: Categorical Profile" — the disclosure-ladder step named the same
 * way the Matches tab and the project's ladder name it (UX-037). A consent must
 * say what is being agreed to, once, in the same words everywhere.
 */
function levelLabel(level: number): string {
  const info = STAGE_INFO[level]
  return info ? `Level ${level}: ${info.name}` : `Level ${level}`
}

interface Suggestion {
  id: string
  text: ReactNode
}

/**
 * The invention that has sat longest without moving, and how long — the
 * figure's second tip ("Bioreactor Flow Control has been waiting 4 days at
 * Draft — pick it up"). Real dates only: a record with no usable updated_at is
 * skipped rather than dated today, and nothing is offered under a day old.
 */
function findStalled(
  projects: Project[],
  agreements: DisclosureAgreement[] | undefined,
): { project: Project; days: number; stage: string } | null {
  const now = Date.now()
  let best: { project: Project; days: number; stage: string } | null = null
  for (const p of projects) {
    if (p.shared_with_me || p.status !== 'active') continue
    const t = new Date(p.updated_at || 0).getTime()
    if (!t || Number.isNaN(t)) continue
    const days = Math.floor((now - t) / 86400000)
    if (days < 1) continue
    const stage = deriveProjectWorkflowStages(p, agreements).find((st) => st.status === 'active')?.name
    if (!stage) continue
    if (!best || days > best.days) best = { project: p, days, stage }
  }
  return best
}

/**
 * The single most useful next action for the Home banner (Fig29): a project
 * whose AI analysis has completed but hasn't been reviewed/approved yet —
 * i.e. sitting at the real "Review" stage of the shared workflow
 * (computeWorkflowStages), the same stage ProjectView itself surfaces as
 * "Next: approve — check the assessment scores and the analysis, then
 * approve to unlock matching."
 * Picks the most recently updated such project so the banner never points at
 * stale work. Returns undefined (no fabricated stage) when nothing qualifies.
 */
function findReviewReadyProject(
  projects: Project[],
  agreements: DisclosureAgreement[] | undefined
): Project | undefined {
  return [...projects]
    // Bug 6783: only the caller's own inventions can be reviewed and approved by them; a project
    // shared in must never be offered as "approve this" (Alice was asked to approve 'Test').
    .filter((p) => p.status === 'active' && !p.shared_with_me && !isProjectDisclosed(p, agreements))
    .sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime())
    .find((p) => {
      const stages = deriveProjectWorkflowStages(p, agreements)
      return stages.some((s) => s.name === 'Review' && s.status === 'active')
    })
}

export default function HomeDashboard({ projects }: { projects: Project[] }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const levels = useDefaultLevels()
  // The tour is a control on the header, the way the figure draws it — NOT a
  // first-run overlay. It used to auto-open, which put a second scrim and a
  // card on top of whatever else the screen was showing; on the invitation
  // screen it covered the sentences a person has to read before accepting.
  const [tourOpen, setTourOpen] = useState(false)

  const { data: profile } = useQuery({ queryKey: ['recommendations'], queryFn: getRecommendations })
  const { data: agreements } = useQuery({ queryKey: ['disclosure-agreements'], queryFn: () => getDisclosureAgreements() })
  const { data: reviews } = useQuery({ queryKey: ['reviews', 'received'], queryFn: () => getReviews('received') })
  // The CACHED overlap only — this read never starts a computation, so Home
  // stays a page that reports rather than a page that spends. When nothing has
  // been computed the tip simply is not offered.
  // The two things /account/sharing can be waiting on. Both use THAT page's own
  // query keys, not keys of Home's own: it owns the accept and the acknowledge,
  // so sharing its cache rows is what makes Home agree with it the instant
  // either is answered — and it is why neither read is a second fetch.
  const { data: invitations } = useQuery({
    queryKey: ['portfolio-invitations'],
    queryFn: listPendingSeats,
    retry: false,
  })
  const { data: ipArrangements } = useQuery({
    queryKey: ['ip-arrangements-mine'],
    queryFn: listMyIpArrangements,
    retry: false,
  })
  const { data: cachedOverlap } = useQuery({
    queryKey: ['cached-portfolio-overlap', 'own'],
    queryFn: () => getCachedPortfolioOverlap('own'),
    retry: false,
  })

  // B1-01: consent/decline must refresh BOTH the Home feed (['disclosure-agreements'])
  // AND any mounted project ladder (['disclosureAgreements', projectId]) so the two
  // screens never disagree about the same request after an action.
  const invalidateDisclosure = () => {
    queryClient.invalidateQueries({ queryKey: ['disclosure-agreements'] })
    queryClient.invalidateQueries({ queryKey: ['disclosureAgreements'] })
  }
  const consentMutation = useMutation({
    mutationFn: (agreementId: string) => consentDisclosureAdvance(agreementId),
    onSuccess: invalidateDisclosure,
  })
  const declineMutation = useMutation({
    mutationFn: (agreementId: string) => declineDisclosureAdvance(agreementId),
    onSuccess: invalidateDisclosure,
  })
  // B1-04: write the profile the POST already returns straight into cache instead
  // of discarding it and refetching — one round-trip fewer, and immune to
  // read-after-write lag / any axios GET quirk on the follow-up fetch.
  const refreshMutation = useMutation({
    mutationFn: () => refreshRecommendations(),
    onSuccess: (profile) => queryClient.setQueryData(['recommendations'], profile),
  })

  // S2-02: a disclosure notification deep-links here with ?view=disclosure-requests
  // (the recipient acts on the request from this section, which is always
  // accessible — unlike the initiator's project the link used to point at).
  // Scroll it into view once the agreements have loaded.
  useEffect(() => {
    if (searchParams.get('view') === 'disclosure-requests' && agreements) {
      document
        .getElementById('pending-disclosure-requests')
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }
  }, [searchParams, agreements])

  // Auto-trigger refresh when profile is null and user has projects with documents
  const hasAutoRefreshed = useRef(false)
  useEffect(() => {
    if (
      !hasAutoRefreshed.current &&
      profile === null &&
      projects.length > 0 &&
      projects.some((p) => p.document_count > 0) &&
      !refreshMutation.isPending
    ) {
      hasAutoRefreshed.current = true
      refreshMutation.mutate()
    }
  }, [profile, projects]) // eslint-disable-line react-hooks/exhaustive-deps

  // BUG-J: a pending advance is only consentable by US when the COUNTERPARTY is
  // ahead (they requested, we haven't consented). When WE are ahead, it's our own
  // outstanding request — show "Awaiting counterparty consent", never a Consent
  // button. Use the viewer-relative levels the backend sets, falling back to the
  // raw org_a/org_b levels only when those are absent.
  const _myLevel = (a: DisclosureAgreement) => (a.my_consented_level !== undefined ? a.my_consented_level : a.org_a_consented_level)
  const _theirLevel = (a: DisclosureAgreement) => (a.counterparty_consented_level !== undefined ? a.counterparty_consented_level : a.org_b_consented_level)
  const pendingRequests = agreements?.filter((a: DisclosureAgreement) => _myLevel(a) < _theirLevel(a)) || []
  const awaitingCounterparty = agreements?.filter((a: DisclosureAgreement) => _myLevel(a) > _theirLevel(a)) || []

  const hasProjects = projects.length > 0
  const noDocsYet = hasProjects && !projects.some((p) => p.document_count > 0)

  // UX-008: a person with no projects is offered the tour once. The tour is
  // opened here (it is a controlled dialog) and GuidedTour's own per-user seen
  // flag, written when it is closed or skipped, is the remembered dismissal.
  // It is held back while a portfolio invitation is waiting (or its read has
  // not answered): an overlay must never cover the sentences a person has to
  // read before accepting, which is why it stopped auto-opening in the first
  // place.
  useEffect(() => {
    if (!hasProjects && invitations !== undefined && invitations.length === 0 && !hasSeenTour('home')) {
      setTourOpen(true)
    }
  }, [hasProjects, invitations])

  // The figure greets the person by their first name. The name is the one on
  // the signed-in token; with none there, the greeting drops the name rather
  // than inventing one or printing a login.
  const firstName = (user?.name ?? '').trim().split(/\s+/)[0] ?? ''
  const greeting = firstName ? `Welcome back, ${firstName}` : 'Welcome back'

  const owned = projects.filter((p) => !p.shared_with_me)
  const sharedWithMe = projects.length - owned.length
  const reviewReadyProject = findReviewReadyProject(projects, agreements)

  // ── The one next step at My inventions ────────────────────────────────────
  //
  // ONE derived spec, computed here so the banner, the rail's pending dot and
  // the "· 1 step waiting" tail all read the same answer. They used to be
  // computed in two places and disagreed: the rail counted only the approve
  // step, so a person with a portfolio invitation waiting saw a banner on
  // /account/sharing and an empty rail on Home in the same build.
  //
  // Priority is the design's set-level queue for Home's own pipeline items,
  // then /account/sharing's own order for the three it shares with that page,
  // so the two screens can never rank the same two things differently.
  const acknowledgementsWaiting = waitingOnMe(ipArrangements ?? [])
  const invitationsWaiting = invitations ?? []
  const nextStep: {
    verb: string
    icon: string
    headline: string
    rule: string
    cta?: { label: string; to?: string; onClick?: () => void }
  } | null = (() => {
    if (noDocsYet) {
      return {
        verb: 'upload',
        icon: '⬆',
        headline: 'Your inventions have no documents yet — add one to start intake and analysis.',
        rule: 'from the pipeline',
        cta: { label: 'Add a document →', to: '/submit' },
      }
    }
    if (reviewReadyProject) {
      return {
        verb: 'approve',
        icon: '✔',
        headline: `'${reviewReadyProject.name}' has completed analysis — approve to unlock matching.`,
        rule: 'from the pipeline',
        cta: { label: 'Approve →', to: `/project/${reviewReadyProject.project_id}?tab=next-steps` },
      }
    }
    // An unanswered IP proposal outranks an invitation for the reason
    // /account/sharing gives: a proposal is a legal record with the caller's
    // name on it, where an invitation left unanswered shows nobody anything.
    if (acknowledgementsWaiting.length > 0) {
      return {
        verb: 'acknowledge',
        icon: '⚖',
        headline: acknowledgeHeadline(acknowledgementsWaiting[0]),
        rule: 'from your IP arrangements · a proposal changes nothing until you acknowledge it',
        cta: { label: 'Read the proposal →', to: '/account/sharing#ip-arrangements' },
      }
    }
    if (invitationsWaiting.length > 0) {
      const first = invitationsWaiting[0]
      return {
        verb: 'accept',
        icon: '✉',
        headline: `${organisationName(first.aggregator_name, first.aggregator_id, first.aggregator_holder)} invites you to join its portfolio — accept it, or decline.`,
        rule: 'from your invitations · joining shows them nothing until you grant a project',
        cta: { label: 'Read the invitation →', to: `/account/sharing?accept=${first.aggregator_id}` },
      }
    }
    if (pendingRequests.length > 0) {
      return {
        verb: 'consent',
        icon: '🤝',
        headline: `You have ${pendingRequests.length} pending disclosure ${pendingRequests.length === 1 ? 'request' : 'requests'} awaiting your response.`,
        rule: 'from your disclosure ladder · nothing advances until you consent',
        cta: {
          label: 'Review requests →',
          onClick: () => document.getElementById('pending-disclosure-requests')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
        },
      }
    }
    return null
  })()

  // The rail says Home is a view of MY INVENTIONS — the widest view of your own
  // set — with the one step waiting on it. Home used to declare nothing, so it
  // was the only inventor screen with no rail and no "You're at" line while
  // /account/sharing beside it had both.
  const zoomInTarget = owned.length > 0
    ? [...owned].sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime())[0]
    : undefined
  useSetSpine({
    level: 'my-inventions',
    levels,
    links: { 'my-inventions': '/projects', invention: zoomInTarget ? `/project/${zoomInTarget.project_id}` : undefined },
    youreAt: ['Home'],
    stepsWaiting: nextStep ? 1 : 0,
    zoomIn: zoomInTarget
      ? { to: `/project/${zoomInTarget.project_id}`, title: 'Zoom in — Invention' }
      : { title: 'Zoom in — you have no invention to open yet' },
    zoomOut: { title: 'This is the widest view of your own inventions' },
    crumb: ['Personal', 'Home'],
  })

  const takeATour = (
    <button
      type="button"
      onClick={() => setTourOpen(true)}
      className="rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-sky-50"
    >
      Take a tour
    </button>
  )

  const primaryAction = (
    <Link
      data-tour="new-invention"
      to="/new"
      className="flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-iflow-800"
    >
      <Plus size={16} /> New invention
    </Link>
  )

  if (!hasProjects) {
    return (
      <div>
        <GuidedTour tourId="home" steps={HOME_TOUR} autoStart={false} open={tourOpen} onClose={() => setTourOpen(false)} />
        <PageHeader
          title={greeting}
          description="Your inventions at a glance — what needs you first, and how they sit together."
          helpKey="home"
          secondaryActions={takeATour}
          primaryAction={primaryAction}
        />
        <EmptyState
          icon={FolderOpen}
          title="No projects yet"
          description="Create your first project to start organising and protecting your innovations. Each project groups the documents, analysis, matches and contacts for one invention."
          action={
            // One click to the create step: this used to open /projects, which
            // said the same words again beside a second Create Project button.
            <Link
              to="/new"
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800"
            >
              <Plus size={16} /> Create project
            </Link>
          }
        />
      </div>
    )
  }

  // profile.document_count is every document uploaded to this person's projects,
  // not a count of documents that have been analysed, so the tile says
  // "uploaded" (UX-016).
  // Bug 6775: the stored profile figure can run ahead of the inventions actually listed
  // (Alice read 15 against 7, Charlie 9 against 7), so the tile adds up the same projects the
  // caption "N of M projects have documents" counts. The profile figure is only the fallback
  // while no projects have loaded.
  const documentsUploaded = owned.length > 0
    ? owned.reduce((sum, p) => sum + (p.document_count || 0), 0)
    : (profile?.document_count ?? 0)
  const projectsWithDocs = owned.filter((p) => p.document_count > 0).length
  const ecosystemMatches = Array.isArray(profile?.recommendations) ? profile.recommendations.length : 0
  const tasksDue = (reviews ?? []).filter((r) => r.status === 'pending' || r.status === 'queued').length

  // Bug 6783: "Recent inventions" lists the caller's own inventions; Roger's list was five projects
  // shared in and none of his own.
  const recentProjects = [...owned]
    .sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime())
    .slice(0, 5)

  // Where the zero-matches caption goes: the Matches tab of the most recently
  // updated active project the reader owns, preferring one not yet discoverable
  // (that is the one a next step actually exists for).
  const ownedActive = [...projects]
    .filter((p) => p.status === 'active' && !p.shared_with_me)
    .sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime())
  const matchesTarget = ownedActive.find((p) => !p.discoverable) ?? ownedActive[0]

  // The figure's two tips, built from records this page already holds. A tip is
  // offered only when the thing it names is real: no cached overlap, no overlap
  // tip; nothing stalled, no stalled tip. The honest empty state below is the
  // answer when none of them fire, and it is a real answer, not a gap.
  const overlapGroups = (cachedOverlap?.cached ? cachedOverlap.groups : []) ?? []
  const stalled = findStalled(projects, agreements)
  const suggestions: Suggestion[] = []
  if (noDocsYet) {
    suggestions.push({ id: 'add-docs', text: 'Add documents to an invention that has none yet, to start intake and AI analysis.' })
  }
  if (overlapGroups.length > 0) {
    const members = overlapGroups[0].member_project_ids.length
    suggestions.push({
      id: 'overlap',
      text: (
        <>
          {members === 2 ? 'Two' : members} of your inventions overlap, not yet analysed —{' '}
          <Link to="/projects?view=overlap" className="font-bold text-iflow-600 hover:text-iflow-700">
            open the group
          </Link>
          .
        </>
      ),
    })
  }
  if (stalled) {
    suggestions.push({
      id: 'stalled',
      text: (
        <>
          <Link to={`/project/${stalled.project.project_id}`} className="font-bold text-iflow-600 hover:text-iflow-700">
            {stalled.project.name}
          </Link>{' '}
          has been waiting {stalled.days} day{stalled.days === 1 ? '' : 's'} at {stalled.stage} — pick it up.
        </>
      ),
    })
  }
  if (pendingRequests.length > 0) {
    suggestions.push({
      id: 'pending',
      text: `You have ${pendingRequests.length} pending disclosure ${pendingRequests.length === 1 ? 'request' : 'requests'} awaiting your response — see below.`,
    })
  }

  const banner = nextStep ? (
    <NextStepBanner
      at="My inventions"
      verb={nextStep.verb}
      icon={nextStep.icon}
      headline={nextStep.headline}
      rule={nextStep.rule}
      cta={nextStep.cta}
    />
  ) : undefined

  return (
    <div>
      <GuidedTour tourId="home" steps={HOME_TOUR} autoStart={false} open={tourOpen} onClose={() => setTourOpen(false)} />
      <PageHeader
        title={greeting}
        description="Your inventions at a glance — what needs you first, and how they sit together."
        helpKey="home"
        secondaryActions={takeATour}
        primaryAction={primaryAction}
        banner={banner}
      />

      {/* The figure's four tiles: Inventions · Documents uploaded · Suggested
          connections · Tasks due. Each caption is a fact about the number above it,
          and when the number is zero the caption says what would make it move
          rather than dressing the zero up.
          Every tile carries real data behind it, so every tile is a door to
          it — Alun's review of build v2 flagged that only "Ecosystem matches"
          was clickable while the other three sat inert with numbers nobody
          could drill into. All four now follow the same pattern. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="home-kpi-tiles">
        <Link to="/projects" className="block text-left" aria-label="Inventions — open My inventions">
          <StatTile
            icon={FolderOpen}
            label="Inventions"
            value={owned.length}
            caption={sharedWithMe > 0
              ? `all yours · ${sharedWithMe} shared with you`
              : 'all yours'}
          />
        </Link>
        <Link to="/upload" className="block text-left" aria-label="Documents uploaded — add documents">
          <StatTile
            icon={FileText}
            label="Documents uploaded"
            value={documentsUploaded}
            // The caption is a fact about the number above it. It used to say
            // "updated 3 hours ago" — when the PROFILE was computed. Nothing the
            // API returns carries a per-week figure, so this says how widely the
            // documents are spread across the reader's own projects.
            caption={
              documentsUploaded === 0
                ? 'none uploaded yet'
                : `${projectsWithDocs} of ${owned.length} ${owned.length === 1 ? 'project has' : 'projects have'} documents`
            }
          />
        </Link>
        {/* The figure draws no "Recommended Connections" section on Home, so the
            tile carries the door to it: the matches themselves live on the
            Ecosystem screen, which is also its own nav item. The capability
            moved; it was not removed. */}
        {ecosystemMatches > 0 ? (
          <Link to="/ecosystem" className="block text-left" aria-label="Suggested connections — open the Ecosystem">
            <StatTile
              icon={GitBranch}
              label="Suggested connections"
              value={ecosystemMatches}
              caption="open the Ecosystem →"
            />
          </Link>
        ) : (
          /* UX-017: a zero here used to be an instruction with no way to do it,
             and the whole tile opened a directory that never mentions
             discoverability. The caption is now the way to do it: a link to the
             Matches tab of a project the reader owns, where Discoverable is
             switched on. A link can't sit inside a button, so at zero the tile
             is not one button; the directory stays reachable as its own link,
             and the Ecosystem explains browsing versus matching on arrival. */
          <div className="text-left" data-testid="ecosystem-matches-zero">
            <StatTile
              icon={GitBranch}
              label="Suggested connections"
              value={ecosystemMatches}
              caption={
                <>
                  0 so far. Approve a project’s analysis, then switch on Discoverable.{' '}
                  <Link
                    to={matchesTarget ? `/project/${matchesTarget.project_id}?tab=matches` : '/projects'}
                    className="font-medium text-iflow-600 hover:text-iflow-700"
                    data-testid="ecosystem-matches-zero-link"
                  >
                    {matchesTarget ? `Open ${matchesTarget.name} →` : 'Open My projects →'}
                  </Link>
                  {' · '}
                  <Link
                    to="/ecosystem?from=home-matches"
                    className="font-medium text-iflow-600 hover:text-iflow-700"
                    data-testid="ecosystem-matches-browse-link"
                  >
                    Browse the directory
                  </Link>
                </>
              }
            />
          </div>
        )}
        <Link to="/tasks" className="block text-left" aria-label="Tasks due — open Tasks & Reviews">
          <StatTile
            icon={ClipboardCheck}
            label="Tasks due"
            value={tasksDue}
            caption={tasksDue > 0 ? 'reviews awaiting your decision' : 'nothing pending'}
          />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent projects. Simplified per Alun's build v2 review: one line
            per row (name + stage), not three — the document count and last-
            updated time are still one click away on the project's own page,
            which is where "the tremendous level of detail" belongs rather
            than in a glance-list meant to be scanned, not read. */}
        <div className="lg:col-span-2 light-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent inventions</h2>
            <Link to="/projects" className="text-sm font-medium text-iflow-600 hover:text-iflow-700">
              View all →
            </Link>
          </div>
          <ul className="divide-y divide-gray-100">
            {recentProjects.map((project) => {
              const pill = getProjectStagePill(project, agreements)
              return (
                <li
                  key={project.project_id}
                  data-testid={`recent-project-${project.project_id}`}
                  onClick={() => navigate(project.shared_with_me ? `/shared/project/${project.project_id}` : `/project/${project.project_id}`)}
                  className="py-3 flex items-center gap-3 cursor-pointer group"
                >
                  <span className="h-9 w-9 shrink-0 rounded-full bg-iflow-100 text-iflow-700 flex items-center justify-center text-xs font-semibold">
                    {projectInitials(project.name)}
                  </span>
                  <p className="min-w-0 flex-1 text-sm font-medium text-gray-900 truncate group-hover:text-iflow-600">
                    {project.name}
                  </p>
                  <StatusChip tone={pill.tone}>{pill.label}</StatusChip>
                </li>
              )
            })}
          </ul>
        </div>

        <div className="space-y-6">
          {/* THE ATLAS SLOT — figure C5, right column, first card.
              An earlier round put this wrapper in the figure's position and
              left the Resources list in it as a placeholder, with the note
              "this card holds the slot the atlas takes when it lands". It has
              landed: the body is now the inventor's own atlas at the slot's
              size, drawn through the same geometry MyInventionsAtlas uses.

              The Resources list is not lost with it — the SAME ResourcesPanel
              opens from the top bar's ? on every screen (AppShell renders it
              for TopBar's onOpenHelp), so nothing that was reachable from this
              card stopped being reachable. C5 draws no Resources card at all;
              it draws this one. */}
          <AtlasSlot projects={projects} documentCount={profile ? documentsUploaded : null} />
          {/* Suggested next steps */}
          <div className="light-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb size={16} className="text-amber-500" />
              <h3 className="text-sm font-semibold text-gray-900">Suggested next steps</h3>
            </div>
            {suggestions.length === 0 ? (
              <p className="text-sm text-gray-600">You&apos;re all caught up — nothing needs attention right now.</p>
            ) : (
              <ol className="space-y-3">
                {suggestions.map((s, i) => (
                  <li key={s.id} className="flex gap-2 text-sm text-gray-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-iflow-100 text-[11px] font-bold text-iflow-700" aria-hidden>
                      {i + 1}
                    </span>
                    <span>{s.text}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

        </div>
      </div>

      {/* Pending Disclosure Requests */}
      {pendingRequests.length > 0 && (
        <div id="pending-disclosure-requests" className="mt-8 scroll-mt-4">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">Pending Disclosure Requests</h2>
            <span className="ml-auto px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-500/20 rounded-full">{pendingRequests.length}</span>
          </div>
          <div className="space-y-3">
            {pendingRequests.map((agreement: DisclosureAgreement) => (
              <div key={agreement.agreement_id} className="light-card p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {agreement.current_level >= 4 ? agreement.counterparty_name || agreement.counterparty_email || 'Organisation' : 'Anonymous organisation'}
                  </p>
                  <div className="mt-1.5">
                    <DisclosureLevel currentLevel={agreement.current_level} pendingAdvance={true} onRequestAdvance={() => {}} compact />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-1.5">
                    Requesting advance to {levelLabel(agreement.current_level + 1)}
                  </p>
                  {STAGE_INFO[agreement.current_level + 1] && (
                    <p className="text-sm text-gray-600 mt-0.5">{STAGE_INFO[agreement.current_level + 1].disclosure}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => consentMutation.mutate(agreement.agreement_id)}
                    disabled={consentMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                  >
                    Consent to Level {agreement.current_level + 1}
                  </button>
                  <button
                    onClick={() => declineMutation.mutate(agreement.agreement_id)}
                    disabled={declineMutation.isPending || consentMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BUG-J: our own outstanding advance requests — awaiting the counterparty.
          No Consent button (you can't consent to your own request). */}
      {awaitingCounterparty.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Hourglass size={16} className="text-sky-500" />
            <h2 className="text-lg font-semibold text-gray-900">Awaiting Counterparty Consent</h2>
            <span className="ml-auto px-2 py-0.5 text-xs font-medium text-sky-700 bg-sky-500/20 rounded-full">{awaitingCounterparty.length}</span>
          </div>
          <div className="space-y-3">
            {awaitingCounterparty.map((agreement: DisclosureAgreement) => (
              <div key={agreement.agreement_id} className="light-card p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {agreement.current_level >= 4 ? agreement.counterparty_name || agreement.counterparty_email || 'Organisation' : 'Anonymous organisation'}
                  </p>
                  <div className="mt-1.5">
                    <DisclosureLevel currentLevel={agreement.current_level} pendingAdvance={true} onRequestAdvance={() => {}} compact />
                  </div>
                  <p className="text-sm text-gray-700 mt-1.5 flex items-center gap-1">
                    <Hourglass size={12} className="text-sky-500" />
                    Awaiting counterparty consent to advance to {levelLabel(agreement.current_level + 1)}
                  </p>
                  {STAGE_INFO[agreement.current_level + 1] && (
                    <p className="text-sm text-gray-600 mt-0.5">{STAGE_INFO[agreement.current_level + 1].disclosure}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}
